import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  getWithdrawalRequestDetail,
  updateWithdrawalRequest,
  WithdrawalRequestDetail,
} from '../services/cashoutApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusBadge = (status: string) => {
  if (status === 'processing') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'failed') return 'bg-rose-100 text-rose-700 border-rose-200';
  if (status === 'cancelled') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const DetailRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className='flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0'>
    <span className='text-sm text-gray-500 shrink-0'>{label}</span>
    <span className='text-sm font-medium text-gray-900 text-right break-all'>{value || '—'}</span>
  </div>
);

const WithdrawalRequestDetailPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<WithdrawalRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [showFailForm, setShowFailForm] = useState(false);
  const [failReason, setFailReason] = useState('');

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const data = await getWithdrawalRequestDetail(requestId);
      setDetail(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load withdrawal request');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (status: 'completed' | 'failed' | 'cancelled' | 'processing', reason?: string) => {
    if (!requestId) return;
    if (status === 'failed' && !reason?.trim()) { toast.error('Failure reason is required'); return; }
    setActionLoading(status);
    try {
      await updateWithdrawalRequest(requestId, status, reason);
      toast.success(status === 'processing' ? 'Reset to processing' : `Marked as ${status}`);
      setShowFailForm(false);
      setFailReason('');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className='min-h-screen bg-gray-50'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-16 text-center'>
          <p className='text-gray-500'>Withdrawal request not found.</p>
          <button onClick={() => navigate('/cashout')} className='mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'>
            ← Back to Cashout
          </button>
        </div>
      </div>
    );
  }

  const pm = detail.payout_method_snapshot;
  const isProcessing = detail.status === 'processing';

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />
      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <button onClick={() => navigate('/cashout')} className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900'>
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' /></svg>
          Back to Cashout
        </button>

        {/* Header */}
        <div className='mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <p className='text-xs text-gray-400 font-mono mb-1'>Withdrawal Request</p>
              <h1 className='text-xl font-bold text-gray-900 font-mono'>{detail.reference_id}</h1>
              <p className='mt-0.5 text-xs text-gray-400 font-mono'>{detail.request_id}</p>
            </div>
            <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${statusBadge(detail.status)}`}>
              {detail.status}
            </span>
          </div>
          <div className='mt-4 flex flex-wrap gap-4 border-t border-gray-100 pt-4 text-sm text-gray-600'>
            <span><span className='font-semibold text-gray-800'>Created:</span> {formatDate(detail.created_at)}</span>
            {detail.processed_at && <span><span className='font-semibold text-gray-800'>Processed:</span> {formatDate(detail.processed_at)}</span>}
            {detail.estimated_arrival_from && (
              <span><span className='font-semibold text-gray-800'>Est. Arrival:</span> {formatDate(detail.estimated_arrival_from)} – {formatDate(detail.estimated_arrival_to)}</span>
            )}
            {detail.failure_reason && (
              <span className='text-rose-600'><span className='font-semibold'>Failure:</span> {detail.failure_reason}</span>
            )}
          </div>
        </div>

        <div className='grid gap-6 lg:grid-cols-3'>
          {/* Left: amounts */}
          <div className='space-y-6'>
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <h2 className='mb-4 text-base font-semibold text-gray-900'>Amounts</h2>
              <DetailRow label='Tokens' value={detail.tokens.toLocaleString()} />
              <DetailRow label='Gross USD' value={`$${detail.gross_usd.toFixed(2)}`} />
              <DetailRow label='Platform Fee' value={`-$${detail.platform_fee_usd.toFixed(2)}`} />
              <DetailRow label='Payout Fee' value={`-$${detail.payout_fee_usd.toFixed(2)}`} />
              <div className='mt-2 flex justify-between border-t border-gray-200 pt-2'>
                <span className='text-sm font-semibold text-gray-700'>Net USD</span>
                <span className='text-sm font-bold text-gray-900'>${detail.net_usd.toFixed(2)}</span>
              </div>
            </div>

            {/* User */}
            {detail.user_profile && (
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <h2 className='mb-3 text-base font-semibold text-gray-900'>User</h2>
                <DetailRow label='Username' value={detail.user_profile.user_name ? `@${detail.user_profile.user_name}` : detail.user_profile.user_id} />
                <DetailRow label='Account Status' value={detail.user_profile.account_status} />
                <button
                  onClick={() => navigate(`/cashout/${encodeURIComponent(detail.user_id)}`)}
                  className='mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50'
                >
                  View Cashout Application →
                </button>
              </div>
            )}
          </div>

          {/* Center: payout method */}
          <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
            <h2 className='mb-4 text-base font-semibold text-gray-900'>Payout Method</h2>
            {pm ? (
              <>
                <DetailRow label='Type' value={pm.type?.toUpperCase()} />
                {pm.display_name && <DetailRow label='Label' value={pm.display_name} />}
                {pm.bank_name && <DetailRow label='Bank' value={pm.bank_name} />}
                {pm.account_holder_name && <DetailRow label='Account Holder' value={pm.account_holder_name} />}
                {pm.account_number_last4 && <DetailRow label='Account No.' value={`****${pm.account_number_last4}`} />}
                {pm.iban_last4 && <DetailRow label='IBAN' value={`****${pm.iban_last4}`} />}
                {pm.swift_bic && <DetailRow label='SWIFT/BIC' value={pm.swift_bic} />}
                {pm.ifsc_code && <DetailRow label='IFSC' value={pm.ifsc_code} />}
                {pm.routing_number && <DetailRow label='Routing No.' value={pm.routing_number} />}
                {pm.sort_code && <DetailRow label='Sort Code' value={pm.sort_code} />}
                {pm.bsb_code && <DetailRow label='BSB' value={pm.bsb_code} />}
                {pm.bank_country && <DetailRow label='Bank Country' value={`${pm.bank_city ? pm.bank_city + ', ' : ''}${pm.bank_country}`} />}
                {pm.currency_code && <DetailRow label='Currency' value={pm.currency_code} />}
                {pm.upi_id && <DetailRow label='UPI ID' value={pm.upi_id} />}
                {pm.paypal_email && <DetailRow label='PayPal' value={pm.paypal_email} />}
                {pm.card_last4 && <DetailRow label='Card' value={`${pm.card_brand || 'Card'} ****${pm.card_last4}`} />}
              </>
            ) : (
              <p className='text-sm text-gray-400'>No payout method snapshot available</p>
            )}
          </div>

          {/* Right: actions */}
          <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
            <h2 className='mb-4 text-base font-semibold text-gray-900'>Actions</h2>
            <div className='space-y-3'>
              {isProcessing && (
                <>
                  <button
                    onClick={() => handleAction('completed')}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    {actionLoading === 'completed' ? 'Marking…' : 'Mark Completed'}
                  </button>
                  <button
                    onClick={() => { setShowFailForm((v) => !v); setFailReason(''); }}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    Mark Failed
                  </button>
                  {showFailForm && (
                    <div className='space-y-2 rounded-lg border border-rose-200 bg-rose-50 p-3'>
                      <textarea
                        value={failReason}
                        onChange={(e) => setFailReason(e.target.value)}
                        rows={2}
                        placeholder='Failure reason (required)'
                        className='w-full rounded-lg border border-rose-300 px-3 py-2 text-sm focus:border-rose-500 focus:outline-none'
                      />
                      <button
                        onClick={() => handleAction('failed', failReason)}
                        disabled={Boolean(actionLoading)}
                        className='w-full rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60'
                      >
                        {actionLoading === 'failed' ? 'Submitting…' : 'Confirm Fail'}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => handleAction('cancelled')}
                    disabled={Boolean(actionLoading)}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    {actionLoading === 'cancelled' ? 'Cancelling…' : 'Cancel'}
                  </button>
                </>
              )}
              {!isProcessing && (
                <button
                  onClick={() => handleAction('processing')}
                  disabled={Boolean(actionLoading)}
                  className='w-full rounded-lg border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  {actionLoading === 'processing' ? 'Resetting…' : 'Reset to Processing'}
                </button>
              )}
              <button
                onClick={load}
                className='w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50'
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalRequestDetailPage;
